import { useEffect, useRef, useState } from 'react';
import { Button } from '@accure/ui';
import { Building2, Loader2, Plus, X } from 'lucide-react';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';

import {
  type EmailCommunication,
  type MeetingCommunication,
  type Site,
} from '@/api/fe-api/entities/communication';
import { UpdateCommunication } from '@/api/fe-api/view/mutations/update-communication';
import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import { useMe } from '@/hooks/use-me';
import { cls } from '@/utils/cls';
import { label, plural } from '@/utils/format/label';

import { Badge } from './ui/badge';

export type CommunicationDetailsLinkedSitesProps = {
  communication: EmailCommunication | MeetingCommunication;
};

export function CommunicationDetailsLinkedSites({
  communication,
}: CommunicationDetailsLinkedSitesProps) {
  const me = useMe();
  const open = useBoolean(false);
  const updateCommunication = UpdateCommunication.useMutation();

  async function handleRemoveSite(siteId: string) {
    const newIds = communication.linkedSites
      .filter((s) => s.id !== siteId)
      .map((site) => site.id);

    await updateCommunication.mutateAsync({
      id: communication.id,
      siteIds: newIds,
    });
  }

  async function handleSelect(newIds: string[]) {
    await updateCommunication.mutateAsync({
      id: communication.id,
      siteIds: newIds,
    });
  }

  return (
    <div className="relative">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-800">
            Linked {plural(me.config.group.labels.name)}
          </h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-primary flex h-7 items-center gap-1 px-2 text-xs"
            onClick={open.setTrue}
          >
            <Plus className="size-3.5" />
            Link {plural(me.config.group.labels.name)}
          </Button>
        </div>

        {communication.linkedSites.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {communication.linkedSites.map((site) => (
              <BadgeSite
                key={site.id}
                site={site}
                onDelete={handleRemoveSite}
              />
            ))}
          </div>
        ) : null}
      </div>

      <SelectSiteModal
        communication={communication}
        open={open.value}
        onSelect={handleSelect}
        onClose={open.setFalse}
      />
    </div>
  );
}

type BadgeSiteProps = {
  site: Site;
  onDelete(id: string): Promise<void>;
};

function BadgeSite({ site, onDelete }: BadgeSiteProps) {
  const loading = useBoolean();

  async function handleDelete() {
    loading.setTrue();
    try {
      await onDelete(site.id);
    } finally {
      loading.setFalse();
    }
  }

  return (
    <Badge
      variant="secondary"
      className="flex items-center gap-1 bg-gray-100 py-1 pl-2 pr-1 text-gray-700"
    >
      <Building2 className="size-3 text-gray-500" />
      <span>{site.name}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="ml-1 size-5 rounded-full p-0 hover:bg-gray-300"
        disabled={loading.value}
        onClick={handleDelete}
      >
        {loading.value ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <X className="size-3" />
        )}
        <span className="sr-only">Remove {site.name}</span>
      </Button>
    </Badge>
  );
}

type SelectSiteModalProps = {
  communication: EmailCommunication | MeetingCommunication;
  open: boolean;
  onSelect(siteIds: string[]): Promise<void>;
  onClose(): void;
};

function SelectSiteModal({
  communication,
  open,
  onSelect,
  onClose,
}: SelectSiteModalProps) {
  const ref = useRef(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>(
    () => communication.linkedSites.map((site) => site.id) ?? [],
  );
  const allSites = GetAllSites.useQuery();
  const loading = useBoolean();
  const me = useMe();

  useEffect(
    function () {
      setSelectedSiteIds(
        communication.linkedSites.map((site) => site.id) ?? [],
      );
    },
    [communication.linkedSites],
  );

  useOnClickOutside(ref, onClose);

  async function handleSubmit() {
    loading.setTrue();
    try {
      await onSelect(selectedSiteIds);
      onClose();
    } finally {
      loading.setFalse();
    }
  }

  function handleCancel() {
    onClose();
  }

  return (
    <div
      className={cls(
        'inset-0 z-50 flex items-center justify-center bg-black/50',
        open ? 'fixed' : 'hidden',
      )}
    >
      <div
        className="mx-auto w-full max-w-md rounded-lg bg-white shadow-lg"
        ref={ref}
      >
        <div className="border-b border-gray-200 p-4">
          <h3 className="text-lg font-medium text-gray-900">
            Link {plural(me.config.group.labels.name)}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Select one or more{' '}
            {plural(me.config.group.labels.name).toLowerCase()} to link to this
            communication
          </p>
        </div>

        <div className="h-60 overflow-y-auto rounded-md border p-4">
          {allSites.isLoading ? (
            <div>loading</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {allSites.data?.map((site) => (
                <div
                  key={site.id}
                  className={cls(
                    'flex cursor-pointer items-center gap-2 px-3 py-2.5 hover:bg-gray-50',
                    {
                      'bg-primary-50': selectedSiteIds.includes(site.id),
                    },
                  )}
                  onClick={() => {
                    setSelectedSiteIds((prev) =>
                      prev.includes(site.id)
                        ? prev.filter((id) => id !== site.id)
                        : [...prev, site.id],
                    );
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedSiteIds.includes(site.id)}
                    onChange={
                      // oxlint-disable-next-line @typescript-eslint/no-empty-function
                      () => {}
                    } // INFO: onChange is done on parent element
                    className="text-primary focus:ring-primary size-4 rounded border-gray-300"
                  />
                  <Building2 className="size-4 shrink-0 text-gray-500" />
                  <span className="text-sm">{site.name}</span>
                </div>
              )) ?? null}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2 border-t border-gray-200 p-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={selectedSiteIds.length === 0 || loading.value}
            onClick={handleSubmit}
          >
            {loading.value ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Saving...
              </>
            ) : selectedSiteIds.length > 0 ? (
              `Link to ${selectedSiteIds.length} ${label(me.config.group.labels.name, selectedSiteIds.length).toLowerCase()}`
            ) : (
              `Link ${plural(me.config.group.labels.name).toLowerCase()}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
